// frontend/src/components/landing/Hero.jsx
import { Suspense, lazy, useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { SITE } from "../../config";
import Button from "../common/Button";
import { ArrowRightIcon, ArrowUpRightIcon } from "../common/Icon";
import "./Hero.css";

const HeroScene = lazy(() => import("./HeroScene"));

/**
 * Landing hero. onStart: called by the primary CTA.
 * Runs the page's single orchestrated load reveal (GSAP timeline).
 */
export default function Hero({ onStart, startLabel = "Start building" }) {
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero__scene-wrap", { opacity: 0, duration: 2.2, ease: "power2.out" }, 0)
        .from(".hero__glow", { opacity: 0, scale: 0.85, duration: 1.8 }, 0)
        .from("[data-reveal]", { opacity: 0, y: 28, duration: 1, stagger: 0.09 }, 0.15)
        .from(".hero__rule", { scaleX: 0, transformOrigin: "left center", duration: 1.2, ease: "power2.inOut" }, 0.5);
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={rootRef} className="hero" aria-labelledby="hero-title">
      <div className="hero__bg" aria-hidden="true">
        <div className="hero__glow" />
        <div className="hero__scene-wrap">
          <Suspense fallback={null}>
            <HeroScene />
          </Suspense>
        </div>
        <div className="hero__grain" />
        <div className="hero__fade" />
      </div>

      <div className="container hero__content">
        <p className="eyebrow hero__eyebrow" data-reveal>
          Multi-agent prompt studio · Research preview
        </p>

        <h1 id="hero-title" className="hero__title">
          <span className="hero__line" data-reveal>Ask the question</span>{" "}
          <span className="hero__line hero__line--accent" data-reveal>you actually mean.</span>
        </h1>

        <p className="hero__lede" data-reveal>
          PAWNI interviews you, studies how experts frame the same task, then drafts, tests and refines your
          prompt with eight specialised agents, so the model hears exactly what you intended.
        </p>

        <div className="hero__ctas" data-reveal>
          <Button variant="primary" size="lg" onClick={onStart} iconRight={<ArrowRightIcon size={18} />}>
            {startLabel}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            href={SITE.paper.url}
            target="_blank"
            rel="noopener noreferrer"
            iconRight={<ArrowUpRightIcon size={18} />}
          >
            Read the research
          </Button>
        </div>

        <div className="hero__rule" aria-hidden="true" />

        <dl className="hero__facts" data-reveal>
          <div>
            <dt>Agents</dt>
            <dd>8</dd>
          </div>
          <div>
            <dt>Prompt elements</dt>
            <dd>18</dd>
          </div>
          <div>
            <dt>Output formats</dt>
            <dd>2</dd>
          </div>
          <div className="hero__facts-paper">
            <dt>Paper</dt>
            <dd className="mono">arXiv:{SITE.paper.arxivId}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}
